import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface PixverseV4T2vParams {
  /**
   * 视频生成提示词
   */
  prompt: string;
  /**
   * 视频宽高比，支持: 16:9, 9:16, 1:1, 4:3, 3:4，默认16:9
   */
  aspect_ratio?: string;
  /**
   * 视频时长（秒），支持5或10秒，默认5秒
   */
  duration?: number;
  /**
   * 视频质量，支持: 270p, 540p, 720p, 1080p，默认540p
   */
  quality?: string;
  /**
   * 负向提示词
   */
  negative_prompt?: string;
  /**
   * 运动模式，支持: normal, fast
   * @values normal | fast
   */
  motion_mode?: 'normal' | 'fast';
  /**
   * 相机运动，支持: zoom_in, zoom_out, pan_left, pan_right, tilt_up, tilt_down
   */
  camera_movement?: string;
  /**
   * 随机种子
   */
  seed?: number;
  /**
   * 模板ID
   */
  template_id?: number;
}

export interface PixverseV4T2vResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * pixverse_v4_t2v
 *
 * PixVerse V4 Text-to-Video Generator
 * 
 * Model: v4
 * Features: 文本生成视频 (Text-to-Video)
 * Aspect Ratios: 16:9, 9:16, 1:1, 4:3, 3:4
 * Duration: 5或10秒
 * Quality: 270p, 540p, 720p, 1080p
 * Motion Modes: normal, fast
 * 
 * API Documentation: https://docs.platform.pixverse.ai/text-to-video-generation-13016634e0
 *
 * @example
 * ```typescript
 * import { initSeacloud, pixverseV4T2v } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await pixverseV4T2v({
 *   // 参数见 PixverseV4T2vParams 接口定义
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 PixverseV4T2vParams 接口定义
 * @returns 生成的资源列表
 */
export async function pixverseV4T2v(
  params: PixverseV4T2vParams,
): Promise<PixverseV4T2vResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 5000,
    maxAttempts: 120,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'pixverse_v4_t2v',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: PixverseV4T2vResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
